import { useRef, useState } from "react"

import { clsx } from "clsx"

import { batteryApiErrorMessage } from "~/api/battery-financing-errors"
import {
  isBatterySystemPricingRequiredError,
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/battery-pricing-errors"
import type {
  BatteryLoanProductDto,
  BatteryProposalFinancingDto,
  BatteryQuoteDto,
  BatterySungageQuotePreviewDto,
} from "~/api/dto/battery-proposal.dto"
import useBatteryProposalPricing from "~/api/hooks/useBatteryProposalPricing"
import useCalculateBatteryProposalPricing from "~/api/hooks/useCalculateBatteryProposalPricing"
import usePreviewBatterySungageQuote from "~/api/hooks/usePreviewBatterySungageQuote"
import useSaveBatterySungageQuote from "~/api/hooks/useSaveBatterySungageQuote"
import { SelectField } from "~/components/ui-components/v2"

import {
  OutOfDateNotice,
  REFRESH_PRICING_BUSY_LABEL,
  REFRESH_PRICING_ERROR_PROMPT,
  REFRESH_PRICING_LABEL,
} from "../pricing-out-of-date"
import { Icon } from "../primitives"
import {
  PREVIEW_SUNGAGE_QUOTE_BUSY_LABEL,
  PREVIEW_SUNGAGE_QUOTE_ERROR_PROMPT,
  SAVE_SUNGAGE_QUOTE_BUSY_LABEL,
  SAVE_SUNGAGE_QUOTE_ERROR_PROMPT,
  SAVE_SUNGAGE_QUOTE_LABEL,
  SAVED_SUNGAGE_QUOTE_LABEL,
  SAVED_SUNGAGE_QUOTE_PRICING_OUT_OF_DATE_PROMPT,
  SUNGAGE_EMPTY_PROMPT,
  SUNGAGE_PAYMENT_OUT_OF_DATE_LABEL,
  SUNGAGE_PREVIEW_PILL,
  SUNGAGE_PREVIEW_REQUIRED_PROMPT,
  SUNGAGE_PRICING_REQUIRED_PROMPT,
  SUNGAGE_PRICING_SUPERSEDED_PROMPT,
  SUNGAGE_READ_ONLY_PROMPT,
  SUNGAGE_SAVE_REPRICES_NOTE,
} from "../sungage/sungage-battery-copy"
import { SungagePaymentDetailsTable } from "../sungage/sungage-payment-details"
import { formatUsdExact } from "../view-helpers"
import {
  btnLink,
  btnPrimary,
  btnSecondary,
  monthlyUsd,
  Pill,
  sectionLabelClass,
} from "./finance-product-chrome"

/**
 * BrightStart half of the Loan Product drawer: pick an eligible product,
 * preview its payment, then explicitly save. Preview never writes the
 * Proposal. Payments are Sungage's; nothing here amortizes them.
 *
 * PowurOS prices the Battery with the product's term and APR, so Save Quote
 * also refreshes pricing when those change. The preview is priced against the
 * current total, so the saved payment can differ from it.
 */
export const SungageBrightStartPicker = ({
  proposalId,
  financing,
  canEdit,
  onClose,
}: {
  proposalId: string
  financing: BatteryProposalFinancingDto
  canEdit: boolean
  /** Drawer Close. Omitted when the picker sits inside initial setup. */
  onClose?: () => void
}): JSX.Element => {
  const pricingQuery = useBatteryProposalPricing(proposalId)
  const previewQuote = usePreviewBatterySungageQuote()
  const saveQuote = useSaveBatterySungageQuote()
  const refreshPricing = useCalculateBatteryProposalPricing()
  const [draftId, setDraftId] = useState<string | null>(null)
  const [preview, setPreview] = useState<BatterySungageQuotePreviewDto | null>(
    null,
  )
  const [saved, setSaved] = useState<`current` | `pricing_out_of_date` | null>(
    null,
  )
  const draftIdRef = useRef<string | null>(null)
  const inFlight = useRef(false)
  draftIdRef.current = draftId

  const quote = financing.quote ?? null
  const selected = financing.selectedLoanProduct ?? null
  const eligible = financing.eligibleLoanProducts
  const settling = pricingQuery.isFetching
  const busy =
    previewQuote.isLoading || saveQuote.isLoading || refreshPricing.isLoading
  const effectiveId = draftId ?? selected?.id ?? null
  const current = eligible.find((product) => product.id === effectiveId) ?? null
  const previewMatchesDraft = preview?.loanProductId === draftId
  // The stored price was requested with the current Quote's term and APR. A
  // preview with other terms is priced against a total Save Quote replaces.
  const pricedTerms = quote?.status === `current` ? quote.loanProduct : null
  const savingReprices =
    pricedTerms?.termYears !== current?.termYears ||
    pricedTerms?.apr !== current?.apr
  const staleSaved = quote?.status === `stale`
  const pricingRequiredError =
    isBatterySystemPricingRequiredError(previewQuote.error) ||
    isBatterySystemPricingRequiredError(saveQuote.error)
  const systemPricingCurrent =
    !settling &&
    !pricingQuery.isError &&
    pricingQuery.data?.pricingStatus === `current`
  const needsSystemPricing =
    pricingRequiredError ||
    (!settling &&
      (pricingQuery.isError ||
        (pricingQuery.isSuccess &&
          pricingQuery.data.pricingStatus !== `current`)))
  const canPreview = canEdit && systemPricingCurrent && !pricingRequiredError
  const submittable = canPreview && previewMatchesDraft && preview != null
  const canRefresh =
    canEdit && (needsSystemPricing || (staleSaved && !submittable))
  const pricingSuperseded =
    isPricingSupersededError(previewQuote.error) ||
    isPricingSupersededError(saveQuote.error)

  const applyPreview = (next: BatterySungageQuotePreviewDto): void => {
    if (next.loanProductId !== draftIdRef.current) {
      return
    }
    setPreview(next)
  }

  const discardBlockedPreview = (
    error: unknown,
    loanProductId: string,
  ): void => {
    if (loanProductId !== draftIdRef.current) {
      return
    }
    if (
      !isPricingSupersededError(error) &&
      !isBatterySystemPricingRequiredError(error)
    ) {
      return
    }
    setPreview(null)
  }

  const requestPreview = (loanProductId: string): void => {
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    previewQuote.mutate(
      { proposalId, body: { loanProductId } },
      {
        onSuccess: applyPreview,
        onError: (error, variables) => {
          discardBlockedPreview(error, variables.body.loanProductId)
        },
        onSettled: () => {
          inFlight.current = false
        },
      },
    )
  }

  const choose = (product: BatteryLoanProductDto): void => {
    if (!canPreview || busy || inFlight.current) {
      return
    }
    previewQuote.reset()
    saveQuote.reset()
    setSaved(null)
    setPreview(null)
    setDraftId(product.id)
    requestPreview(product.id)
  }

  const retryPreview = (): void => {
    if (!canPreview || busy || inFlight.current || draftId == null) {
      return
    }
    previewQuote.reset()
    saveQuote.reset()
    requestPreview(draftId)
  }

  const submit = (): void => {
    if (!submittable || busy || inFlight.current || preview == null) {
      return
    }
    inFlight.current = true
    saveQuote.mutate(
      {
        proposalId,
        body: {
          loanProductId: preview.loanProductId,
          pricingContext: preview.pricingContext,
        },
      },
      {
        onSuccess: (financing) => {
          setDraftId(null)
          setPreview(null)
          // The selection is saved even when the pricing refresh behind it
          // fails; the Quote then comes back stale and the seller is told. A
          // refreshed price outside the product's limits also leaves it stale,
          // but the drawer's status banner already names that.
          setSaved(
            financing.quote?.status === `current` &&
              financing.quote.paymentDetails != null
              ? `current`
              : financing.quote?.status === `stale` &&
                  financing.selectionStatus === `loan_product_selected`
                ? `pricing_out_of_date`
                : null,
          )
        },
        onError: (error) => {
          if (
            isPricingSupersededError(error) ||
            isBatterySystemPricingRequiredError(error)
          ) {
            setPreview(null)
          }
        },
        onSettled: () => {
          inFlight.current = false
        },
      },
    )
  }

  const previewError =
    previewQuote.isError && !pricingSuperseded && !pricingRequiredError
      ? (batteryApiErrorMessage(previewQuote.error) ??
        PREVIEW_SUNGAGE_QUOTE_ERROR_PROMPT)
      : null
  const saveError =
    saveQuote.isError && !pricingSuperseded && !pricingRequiredError
      ? (batteryApiErrorMessage(saveQuote.error) ??
        SAVE_SUNGAGE_QUOTE_ERROR_PROMPT)
      : null

  return (
    <>
      <div className="flex flex-col gap-3">
        <h3 className={sectionLabelClass}>Loan Product</h3>

        {selected ? (
          <SavedBrightStart
            product={selected}
            quote={quote}
            previewing={draftId != null && draftId !== selected.id}
          />
        ) : null}

        <SelectField
          label="Loan Product"
          labelSrOnly
          portal={false}
          disabled={!canPreview || busy}
          options={eligible}
          value={current}
          onChange={choose}
          getKey={(product) => product.id}
          getLabel={(product) => product.name}
          placeholder={`Select a Loan Product`}
        />

        {!canEdit ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {SUNGAGE_READ_ONLY_PROMPT}
          </p>
        ) : null}

        {eligible.length === 0 ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {SUNGAGE_EMPTY_PROMPT}
          </p>
        ) : null}

        {needsSystemPricing ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {SUNGAGE_PRICING_REQUIRED_PROMPT}
          </p>
        ) : canEdit && !settling && draftId == null && eligible.length > 0 ? (
          <p
            role="status"
            className="text-[length:var(--step--2)] text-bp-text-muted"
          >
            {SUNGAGE_PREVIEW_REQUIRED_PROMPT}
          </p>
        ) : null}

        {previewQuote.isLoading ? (
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            {PREVIEW_SUNGAGE_QUOTE_BUSY_LABEL}
          </p>
        ) : null}

        {pricingSuperseded || previewError ? (
          <div className="flex flex-col items-start gap-2">
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {pricingSuperseded
                ? SUNGAGE_PRICING_SUPERSEDED_PROMPT
                : previewError}
            </p>
            {draftId != null && canPreview ? (
              <button type="button" onClick={retryPreview} className={btnLink}>
                Retry
              </button>
            ) : null}
          </div>
        ) : null}

        {previewMatchesDraft && preview != null && current ? (
          <>
            <PreviewBrightStart product={current} preview={preview} />
            {savingReprices ? (
              <p
                role="note"
                className="text-[length:var(--step--2)] text-bp-text-muted"
              >
                {SUNGAGE_SAVE_REPRICES_NOTE}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="mt-auto flex shrink-0 flex-col gap-3 border-t border-bp-border pt-5">
        {saveError ? (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2"
          >
            <span className="text-[length:var(--step--2)] text-bp-danger">
              {saveError}
            </span>
            <button
              type="button"
              disabled={!submittable || busy}
              onClick={submit}
              className="shrink-0 rounded-md border border-bp-danger/50 px-3 py-1 text-[length:var(--step--2)] font-medium text-bp-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        ) : null}
        {refreshPricing.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {isPricingSupersededError(refreshPricing.error)
              ? PRICING_SUPERSEDED_MESSAGE
              : REFRESH_PRICING_ERROR_PROMPT}
          </p>
        ) : null}
        {saved === `current` ? (
          <p
            role="status"
            className="flex items-center gap-1.5 text-[length:var(--step--2)] font-medium text-bp-success"
          >
            <Icon name="check" className="size-4" />
            {SAVED_SUNGAGE_QUOTE_LABEL}
          </p>
        ) : saved === `pricing_out_of_date` && staleSaved ? (
          <OutOfDateNotice
            label={SAVED_SUNGAGE_QUOTE_PRICING_OUT_OF_DATE_PROMPT}
            testId="sungage-saved-pricing-out-of-date"
          />
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          {onClose != null ? (
            <button type="button" onClick={onClose} className={btnSecondary}>
              Close
            </button>
          ) : (
            <span />
          )}
          {canEdit && (eligible.length > 0 || canRefresh) ? (
            submittable ? (
              <button
                type="button"
                disabled={busy}
                onClick={submit}
                className={btnPrimary}
              >
                {saveQuote.isLoading
                  ? SAVE_SUNGAGE_QUOTE_BUSY_LABEL
                  : SAVE_SUNGAGE_QUOTE_LABEL}
              </button>
            ) : canRefresh ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  if (inFlight.current) {
                    return
                  }
                  inFlight.current = true
                  saveQuote.reset()
                  previewQuote.reset()
                  setSaved(null)
                  refreshPricing.mutate(
                    { proposalId },
                    {
                      onSettled: () => {
                        inFlight.current = false
                      },
                    },
                  )
                }}
                className={btnPrimary}
              >
                {refreshPricing.isLoading
                  ? REFRESH_PRICING_BUSY_LABEL
                  : REFRESH_PRICING_LABEL}
              </button>
            ) : (
              <button type="button" disabled className={btnPrimary}>
                {SAVE_SUNGAGE_QUOTE_LABEL}
              </button>
            )
          ) : null}
        </div>
      </div>
    </>
  )
}

const SavedBrightStart = ({
  product,
  quote,
  previewing,
}: {
  product: BatteryLoanProductDto
  quote: BatteryQuoteDto | null
  previewing: boolean
}): JSX.Element => {
  const details = quote?.paymentDetails ?? null
  const payment =
    details?.initialMonthlyPayment ?? quote?.estimatedMonthlyPayment ?? null

  return (
    <div
      data-testid="sungage-saved-quote"
      className={clsx(
        `rounded-lg border px-3.5 py-3`,
        quote?.status === `unavailable`
          ? `border-bp-danger/40 bg-bp-danger/5`
          : `border-bp-border bg-bp-elevated`,
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
            {product.name}
          </span>
          {payment != null ? (
            <span className="flex flex-wrap items-center gap-2 text-[length:var(--step--2)] text-bp-text-muted">
              <span className="text-bp-text tabular-nums">
                {`${monthlyUsd.format(payment)}/mo`}
              </span>
              {quote?.status === `stale` ? (
                <OutOfDateNotice label={SUNGAGE_PAYMENT_OUT_OF_DATE_LABEL} />
              ) : null}
            </span>
          ) : quote?.status === `unavailable` ? (
            <span className="text-[length:var(--step--2)] text-bp-danger">
              Payment unavailable
            </span>
          ) : null}
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <Pill tone="muted">Saved</Pill>
          {previewing ? <Pill tone="warn">Unchanged</Pill> : null}
        </span>
      </div>
      {details != null && !previewing ? (
        <div className="mt-3 border-t border-bp-border/60 pt-2">
          <SungagePaymentDetailsTable details={details} />
        </div>
      ) : null}
    </div>
  )
}

const PreviewBrightStart = ({
  product,
  preview,
}: {
  product: BatteryLoanProductDto
  preview: BatterySungageQuotePreviewDto
}): JSX.Element => (
  <div
    data-testid="sungage-preview-quote"
    className="rounded-lg border border-bp-accent bg-bp-accent/10"
  >
    <div className="flex items-center justify-between gap-3 border-b border-bp-border px-4 py-3">
      <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
        {product.name}
      </span>
      <Pill tone="warn">{SUNGAGE_PREVIEW_PILL}</Pill>
    </div>
    <SungagePaymentDetailsTable
      details={preview.paymentDetails}
      extras={[
        {
          label: `Total project cost`,
          value: formatUsdExact(preview.totalProjectCost),
        },
        {
          label: `Battery amount`,
          value: formatUsdExact(preview.batteryAmount),
        },
      ]}
    />
  </div>
)
